import React, { createContext, useContext, useState, useCallback } from 'react';

export type Language = 'en' | 'id';

const STORAGE_KEY = 'app_language';

// ─── Translation shape ────────────────────────────────────────────────────────

export interface TranslationKeys {
    // ── Common / shared ──
    on: string;
    off: string;
    pending: string;
    active: string;
    start: string;
    end: string;
    total: string;
    hours: string;
    shift: string;
    office: string;
    location: string;
    camera: string;
    notifications: string;
    coordinates: string;
    radius: string;
    loading: string;
    retry: string;
    refresh: string;
    cancel: string;
    submit: string;
    onTime: string;
    late: string;
    absent: string;
    checkIn: string;
    checkOut: string;
    duration: string;
    permission: string;
    language: string;
    english: string;
    indonesian: string;
    error: string;
    myAccount: string;

    // ── Navigation ──
    navHome: string;
    navAttendance: string;
    navRequests: string;
    navSettings: string;
    navProfile: string;

    // ── Login ──
    loginTitle: string;
    loginSubtitle: string;
    loginWelcome: string;
    loginDesc: string;
    loginEmail: string;
    loginEmailPlaceholder: string;
    loginPassword: string;
    loginPasswordPlaceholder: string;
    loginSignIn: string;
    loginSigningIn: string;
    loginNeedAccess: string;
    loginContactHR: string;
    developedBy: string;
    loginFillFields: string;
    loginSuccess: string;
    loginSuccessDesc: string;
    loginFailed: string;

    // ── Dashboard ──
    greetMorning: string;
    greetAfternoon: string;
    greetEvening: string;
    greetNight: string;
    enableReminders: string;
    enableRemindersDesc: string;
    enable: string;
    remindersOn: string;
    notifBlocked: string;
    todaysAttendance: string;
    notCheckedInYet: string;
    notCheckedInDesc: string;
    photoVerification: string;
    checkInNow: string;
    workComplete: string;
    workCompleteDesc: string;
    currentlyWorking: string;
    currentlyWorkingDesc: string;
    shiftProgress: string;
    checkInTime: string;
    workingDuration: string;
    checkOutBtn: string;
    liveLocation: string;
    liveLocationDesc: string;
    loadingMap: string;
    thisWeek: string;
    weeklyStreak: string;
    performance: string;
    thisMonthRate: string;
    onTimeDays: string;
    lateDays: string;
    totalHours: string;
    todaysSchedule: string;
    regularShift: string;
    earlyCheckout: string;
    earlyCheckoutDesc: (time: string, label: string) => string;
    earlyCheckoutWarning: string;
    earlyCheckoutConfirm: string;
    stayWorking: string;
    checkOutEarly: string;
    checkInBlockedTitle: string;
    checkInBlockedSubtitle: string;
    checkInBlockedNoSite: string;
    checkInBlockedNoSiteDesc: string;
    checkInBlockedNoShift: string;
    checkInBlockedNoShiftDesc: string;
    checkInBlockedContact: string;
    checkInBlockedUnderstood: string;
    checkedInToast: string;
    checkedInDesc: string;
    checkInFailed: string;
    checkedOutToast: string;
    checkedOutDesc: string;
    checkOutFailed: string;
    refreshed: string;
    refreshedDesc: string;
    refreshFailed: string;
    refreshFailedDesc: string;
    notifEnabled: string;
    notifEnabledDesc: string;
    notifPermDenied: string;
    notifPermDeniedDesc: string;
    tooEarlyTitle: string;
    tooEarlyDesc: string;
    tooEarlyGotIt: string;

    // ── Dashboard widgets ──
    dashPendingRequests: string;
    dashPendingRequestsDesc: string;
    dashNoPending: string;
    dashNoPendingDesc: string;
    dashViewAll: string;
    dashLatestPayslip: string;
    dashLatestPayslipDesc: string;
    dashNoPayslip: string;
    dashNoPayslipDesc: string;
    dashViewPayslips: string;
    dashOvertimeThisMonth: string;
    dashOvertimeThisMonthDesc: string;
    dashApprovedHours: string;
    dashApprovedRequests: string;
    dashNoOvertime: string;
    dashNoOvertimeDesc: string;
    dashPendingOt: string;

    // ── Attendance ──
    attendanceHistory: string;
    attendanceHistoryDesc: string;
    thisMonth: string;
    totalDaysWorked: string;
    average: string;
    avgPerDay: (h: string) => string;
    monthlyOverview: string;
    monthlyOverviewDesc: string;
    upcoming: string;
    filterAndTrend: string;
    all: string;
    month: string;
    week: string;
    totalRecords: string;
    noRecords: string;
    noRecordsDesc: string;
    record: string;
    records: string;
    checkInLocation: string;
    checkOutLocation: string;
    fromOffice: string;
    lateBy: (min: number) => string;
    earlyBy: (min: number) => string;
    payslips: string;

    // ── Requests ──
    myRequests: string;
    myRequestsDesc: string;
    totalRequests: string;
    pendingRequests: string;
    approved: string;
    rejected: string;
    cancelled: string;
    loadingRequests: string;
    statusDistribution: string;
    requestStatusBreakdown: string;
    requestsByType: string;
    requestTypeBreakdown: string;
    leave: string;
    overtime: string;
    salaryAdv: string;
    leaveRequests: string;
    leaveRequestsDesc: string;
    newLeave: string;
    newLeaveTitle: string;
    newLeaveDesc: string;
    leaveType: string;
    selectLeaveType: string;
    startDate: string;
    endDate: string;
    reason: string;
    reasonPlaceholder: string;
    submitRequest: string;
    overtimeRequests: string;
    overtimeRequestsDesc: string;
    newOvertime: string;
    newOvertimeTitle: string;
    newOvertimeDesc: string;
    date: string;
    otTotalHours: string;
    otMaxHours: string;
    salaryAdvance: string;
    salaryAdvanceDesc: string;
    newRequest: string;
    newSalaryTitle: string;
    newSalaryDesc: string;
    amount: string;
    salaryReasonPlaceholder: string;
    noRequests: (type: string) => string;
    noRequestsDesc: string;
    submitted: (rel: string) => string;
    from: string;
    to: string;
    cancelRequest: string;
    cancelRequestDesc: string;
    keepRequest: string;
    yesCancel: string;
    fillAllFields: string;
    endDateAfterStart: string;
    fillValidValues: string;
    otMaxExceeded: string;
    leaveSubmitted: string;
    overtimeSubmitted: string;
    salarySubmitted: string;
    shiftSwapSubmitted: string;

    // ── Shift Swap ──
    shiftSwap: string;
    newShiftSwap: string;
    newShiftSwapTitle: string;
    newShiftSwapDesc: string;
    swapDate: string;
    targetEmployee: string;
    selectColleague: string;
    swapReason: string;
    swapReasonPlaceholder: string;
    shiftSwapRejected: string;
    noShiftSwapRequests: string;
    requesterShift: string;
    targetShift: string;
    loadingColleagues: string;
    noColleaguesFound: string;
    selectDateFirst: string;

    // ── Profile ──
    punctuality: string;
    daysAttended: string;
    leavesUsed: string;
    personalInfo: string;
    personalInfoDesc: string;
    email: string;
    phone: string;
    birthDate: string;
    gender: string;
    address: string;
    notProvided: string;
    employmentDetails: string;
    employmentDetailsDesc: string;
    employeeCode: string;
    department: string;
    organization: string;
    joined: string;
    performanceTitle: string;
    days: string;
    avgHoursDay: string;
    accountIds: string;
    userUuid: string;
    orgCode: string;
    logout: string;
    newEmployee: string;

    // ── Payslips ──
    loadingPayslips: string;
    noPayslips: string;
    noPayslipsDesc: string;
    netSalary: string;
    earnings: string;
    basicSalary: string;
    overtimePay: string;
    allowances: string;
    grossSalary: string;
    deductions: string;
    salaryAdvanceDeduction: string;
    totalDeductions: string;
    downloading: string;
    downloadPayslip: string;
    paid: string;
    confirmed: string;
    closed: string;
    prorated: string;
    failedLoadPayslips: string;

    // ── Settings (existing) ──
    settings: string;
    settingsDesc: string;
    managedByOrg: string;
    managedByOrgDesc: string;
    readOnly: string;
    languageDesc: string;
    officeLocation: string;
    officeLocationDesc: string;
    loadingOfficeLocations: string;
    noOfficeLocations: string;
    noOfficeLocationsDesc: string;
    gpsCoordinates: string;
    geofenceRadius: string;
    meters: string;
    mustBeWithin: (dist: string) => string;
    refreshLocations: string;
    shiftSchedule: string;
    shiftScheduleDesc: string;
    loadingShiftSchedule: string;
    notAssigned: string;
    noShiftAssigned: string;
    noShiftAssignedDesc: string;
    activeShift: string;
    upcomingShift: string;
    activeFrom: (date: string) => string;
    effectiveFrom: (date: string) => string;
    refreshShiftSchedule: string;
    notificationsDesc: string;
    browserNotificationsUnsupported: string;
    dailyReminder: string;
    notifActive: string;
    notifDisabled: string;
    preShiftReminder: string;
    preShiftReminderDesc: string;
    lateCheckinAlert: string;
    lateCheckinAlertDesc: string;
    postCheckinConfirmation: string;
    postCheckinConfirmationDesc: string;
    notifGranted: string;
    notifDenied: string;
    notifDefault: string;
    notifUnsupported: string;
    testNotif: string;
    allowNotif: string;
    notifBlockedSettings: string;
    appPermissions: string;
    appPermissionsDesc: string;
    locationDesc: string;
    cameraDesc: string;
    toggleLocation: string;
    toggleCamera: string;
    requesting: string;
    allowLocation: string;
    allowCamera: string;
    locationGrantedInfo: string;
    locationDenied: string;
    cameraGrantedInfo: string;
    cameraDenied: string;
    allPermissionsGranted: string;
    refreshPermissions: string;
    permGranted: string;
    permDenied: string;
    permPrompt: string;
    permUnsupported: string;
    currentConfig: string;
    // Camera Capture Modal
    cameraTitle: string;
    checkOutCameraTitle: string;
    cameraModalDesc: string;
    cameraPermDenied: string;
    cameraNotFound: string;
    cameraAccessFailed: string;
    openingCamera: string;
    switchCamera: string;
    tryAgain: string;
    openCamera: string;
    capturePhoto: string;
    retakePhoto: string;
    usePhoto: string;
    close: string;
}

// ─── Translation dictionaries ─────────────────────────────────────────────────

const translations: Record<Language, TranslationKeys> = {
    en: {
        // Common
        on: 'On',
        off: 'Off',
        pending: 'pending',
        active: 'Active',
        start: 'Start',
        end: 'End',
        total: 'total',
        hours: 'Hours',
        shift: 'Shift',
        office: 'Office',
        location: 'Location',
        camera: 'Camera',
        notifications: 'Notifications',
        coordinates: 'Coordinates',
        radius: 'Radius',
        loading: 'Loading...',
        retry: 'Retry',
        refresh: 'Refresh',
        cancel: 'Cancel',
        submit: 'Submit',
        onTime: 'On Time',
        late: 'Late',
        absent: 'Absent',
        checkIn: 'Check In',
        checkOut: 'Check Out',
        duration: 'Duration',
        permission: 'Permission',
        language: 'Language',
        english: 'English',
        indonesian: 'Bahasa Indonesia',
        error: 'An error occurred',
        myAccount: 'My Account',

        // Navigation
        navHome: 'Home',
        navAttendance: 'Attendance',
        navRequests: 'Requests',
        navSettings: 'Settings',
        navProfile: 'Profile',

        // Login
        loginTitle: 'Hadir',
        loginSubtitle: 'Smart attendance management system',
        loginWelcome: 'Welcome Back',
        loginDesc: 'Enter your credentials to access your account',
        loginEmail: 'Email or Username',
        loginEmailPlaceholder: 'john@company.com or john123',
        loginPassword: 'Password',
        loginPasswordPlaceholder: 'Enter your password',
        loginSignIn: 'Sign In',
        loginSigningIn: 'Signing in...',
        loginNeedAccess: 'Need access?',
        loginContactHR: 'Contact HR',
        developedBy: 'Developed by',
        loginFillFields: 'Please fill in all fields',
        loginSuccess: 'Welcome back!',
        loginSuccessDesc: 'You have successfully logged in.',
        loginFailed: 'Login Failed',

        // Dashboard
        greetMorning: 'Good Morning',
        greetAfternoon: 'Good Afternoon',
        greetEvening: 'Good Evening',
        greetNight: 'Good Night',
        enableReminders: 'Enable Attendance Reminders',
        enableRemindersDesc: 'Get notified 5 minutes before your shift starts',
        enable: 'Enable',
        remindersOn: 'Reminders on',
        notifBlocked: 'Notifications blocked',
        todaysAttendance: "Today's Attendance",
        notCheckedInYet: 'Not Checked In Yet',
        notCheckedInDesc: "You haven't checked in yet today",
        photoVerification: 'A photo will be taken for verification',
        checkInNow: 'Check In Now',
        workComplete: 'Work Complete',
        workCompleteDesc: 'Great job today! Have a wonderful rest.',
        currentlyWorking: 'Currently Working',
        currentlyWorkingDesc: "You're doing great, keep it up!",
        shiftProgress: 'Shift Progress',
        checkInTime: 'Check In Time',
        workingDuration: 'Working Duration',
        checkOutBtn: 'Check Out',
        liveLocation: 'Live Location & Geofence',
        liveLocationDesc: 'Your real-time position relative to the office. You must be within the geofence to check in.',
        loadingMap: 'Loading map...',
        thisWeek: 'This Week',
        weeklyStreak: 'Your weekly attendance streak',
        performance: 'Performance',
        thisMonthRate: "This month's attendance rate",
        onTimeDays: 'On Time Days',
        lateDays: 'Late Days',
        totalHours: 'Total Hours',
        todaysSchedule: "Today's Schedule",
        regularShift: 'Regular Shift',
        earlyCheckout: 'Early Check-Out Confirmation',
        earlyCheckoutDesc: (time, label) => `Your shift today ends at ${time}. You are preparing to check out ${label} earlier than scheduled.`,
        earlyCheckoutWarning: 'Please note that checking out at this time will be recorded as an early departure on your attendance history.',
        earlyCheckoutConfirm: 'Are you sure you want to proceed with checking out now?',
        stayWorking: 'Cancel & Continue Working',
        checkOutEarly: 'Yes, Check Out',
        checkInBlockedTitle: 'Cannot Check In',
        checkInBlockedSubtitle: 'Your account is not fully configured. Please contact your HR or Admin to resolve the following issue(s) before you can check in:',
        checkInBlockedNoSite: 'No Office Site Assigned',
        checkInBlockedNoSiteDesc: 'You have not been assigned to any office site. The system requires a valid office location to verify your geofence position during check-in.',
        checkInBlockedNoShift: 'No Shift Schedule Assigned',
        checkInBlockedNoShiftDesc: 'You have not been assigned to a work shift. The system requires a shift schedule to record on-time or late status for your attendance.',
        checkInBlockedContact: 'Please contact your HR or Admin to assign the required configuration to your account.',
        checkInBlockedUnderstood: 'Understood',
        checkedInToast: 'Checked In!',
        checkedInDesc: 'You have successfully checked in for today.',
        checkInFailed: 'Check-In Failed',
        checkedOutToast: 'Checked Out!',
        checkedOutDesc: 'You have successfully checked out. Have a great day!',
        checkOutFailed: 'Check-Out Failed',
        refreshed: 'Refreshed',
        refreshedDesc: 'Data has been updated.',
        refreshFailed: 'Refresh Failed',
        refreshFailedDesc: 'Unable to refresh data.',
        notifEnabled: 'Notifications enabled!',
        notifEnabledDesc: "You'll be reminded before your shift starts.",
        notifPermDenied: 'Permission denied',
        notifPermDeniedDesc: 'Enable notifications in your browser settings.',
        tooEarlyTitle: 'Too Early to Check In',
        tooEarlyDesc: 'You can only check in up to 1 hour before your scheduled shift starts.',
        tooEarlyGotIt: 'Got It',

        // Dashboard widgets
        dashPendingRequests: 'Pending Requests',
        dashPendingRequestsDesc: 'Requests awaiting approval',
        dashNoPending: 'All Clear!',
        dashNoPendingDesc: 'No pending requests at the moment.',
        dashViewAll: 'View All',
        dashLatestPayslip: 'Latest Payslip',
        dashLatestPayslipDesc: 'Your most recent salary slip',
        dashNoPayslip: 'No Payslips Yet',
        dashNoPayslipDesc: 'Your payslips will appear here once processed.',
        dashViewPayslips: 'View Payslips',
        dashOvertimeThisMonth: 'Overtime This Month',
        dashOvertimeThisMonthDesc: 'Your approved overtime summary',
        dashApprovedHours: 'Approved Hours',
        dashApprovedRequests: 'Approved Requests',
        dashNoOvertime: 'No Overtime',
        dashNoOvertimeDesc: 'No overtime recorded this month.',
        dashPendingOt: 'Pending',

        // Attendance
        attendanceHistory: 'Attendance History',
        attendanceHistoryDesc: 'View and track your attendance records',
        thisMonth: 'This Month',
        totalDaysWorked: 'Total Days Worked',
        average: 'Average',
        avgPerDay: (h) => `Avg ${h}h/day`,
        monthlyOverview: 'Monthly Overview',
        monthlyOverviewDesc: 'each square is one day',
        upcoming: 'Upcoming',
        filterAndTrend: 'Filter & Trend',
        all: 'All',
        month: 'Month',
        week: 'Week',
        totalRecords: 'Total Records',
        noRecords: 'No Records Found',
        noRecordsDesc: "You don't have any attendance records for the selected period. Try changing the filter or check in to create your first record.",
        record: 'record',
        records: 'records',
        checkInLocation: 'Check In Location',
        checkOutLocation: 'Check Out Location',
        fromOffice: 'from office',
        lateBy: (min) => `Late by ${min} min`,
        earlyBy: (min) => `Early by ${min} min`,
        payslips: 'Payslips',

        // Requests
        myRequests: 'My Requests',
        myRequestsDesc: 'Submit and track your leave, overtime, and salary advance requests',
        totalRequests: 'Total Requests',
        pendingRequests: 'Pending',
        approved: 'Approved',
        rejected: 'Rejected',
        cancelled: 'Cancelled',
        loadingRequests: 'Loading requests...',
        statusDistribution: 'Status Distribution',
        requestStatusBreakdown: 'Breakdown of request statuses',
        requestsByType: 'Requests by Type',
        requestTypeBreakdown: 'Distribution across request types',
        leave: 'Leave',
        overtime: 'Overtime',
        salaryAdv: 'Salary Adv.',
        leaveRequests: 'Leave Requests',
        leaveRequestsDesc: 'Request time off from work',
        newLeave: 'New Leave',
        newLeaveTitle: 'New Leave Request',
        newLeaveDesc: 'Fill in the details below to submit a leave request.',
        leaveType: 'Leave Type',
        selectLeaveType: 'Select leave type',
        startDate: 'Start Date',
        endDate: 'End Date',
        reason: 'Reason',
        reasonPlaceholder: 'Describe the reason for your leave...',
        submitRequest: 'Submit Request',
        overtimeRequests: 'Overtime Requests',
        overtimeRequestsDesc: 'Log extra working hours',
        newOvertime: 'New Overtime',
        newOvertimeTitle: 'New Overtime Request',
        newOvertimeDesc: 'Specify the date and hours of overtime worked.',
        date: 'Date',
        otTotalHours: 'Total Hours',
        otMaxHours: 'Maximum 12 hours per request',
        salaryAdvance: 'Salary Advance',
        salaryAdvanceDesc: 'Request an early salary disbursement',
        newRequest: 'New Request',
        newSalaryTitle: 'New Salary Advance Request',
        newSalaryDesc: 'Specify the amount and reason for your advance request.',
        amount: 'Amount (Rp)',
        salaryReasonPlaceholder: 'Describe why you need a salary advance...',
        noRequests: (type) => `No ${type} requests yet`,
        noRequestsDesc: 'Click the button above to submit your first request',
        submitted: (rel) => `Submitted ${rel}`,
        from: 'From',
        to: 'To',
        cancelRequest: 'Cancel Request',
        cancelRequestDesc: 'Are you sure you want to cancel this request? This action cannot be undone.',
        keepRequest: 'Keep Request',
        yesCancel: 'Yes, Cancel',
        fillAllFields: 'Please fill all fields',
        endDateAfterStart: 'End date must be after start date',
        fillValidValues: 'Please fill all fields with valid values',
        otMaxExceeded: 'Overtime cannot exceed 12 hours',
        leaveSubmitted: 'Leave request submitted',
        overtimeSubmitted: 'Overtime request submitted',
        salarySubmitted: 'Salary advance request submitted',
        shiftSwapSubmitted: 'Shift swap request submitted',

        // Shift Swap
        shiftSwap: 'Shift Swap',
        newShiftSwap: 'New Swap',
        newShiftSwapTitle: 'Request Shift Swap',
        newShiftSwapDesc: 'Select a colleague and date to swap shifts',
        swapDate: 'Swap Date',
        targetEmployee: 'Swap With',
        selectColleague: 'Select colleague...',
        swapReason: 'Reason (Optional)',
        swapReasonPlaceholder: 'Why do you need to swap shifts?',
        shiftSwapRejected: 'Rejected',
        noShiftSwapRequests: 'No shift swap requests yet',
        requesterShift: 'Your Shift',
        targetShift: 'Their Shift',
        loadingColleagues: 'Loading colleagues...',
        noColleaguesFound: 'No colleagues found',
        selectDateFirst: 'Select a swap date first',

        // Profile
        punctuality: 'Punctuality',
        daysAttended: 'Days Attended',
        leavesUsed: 'Leaves Used',
        personalInfo: 'Personal Information',
        personalInfoDesc: 'Your personal details',
        email: 'Email',
        phone: 'Phone',
        birthDate: 'Birth Date',
        gender: 'Gender',
        address: 'Address',
        notProvided: 'Not provided',
        employmentDetails: 'Employment Details',
        employmentDetailsDesc: 'Your work information and identifiers',
        employeeCode: 'Employee Code',
        department: 'Department',
        organization: 'Organization',
        joined: 'Joined',
        performanceTitle: 'Performance',
        days: 'days',
        avgHoursDay: 'Avg Hours/Day',
        accountIds: 'Account IDs',
        userUuid: 'User UUID',
        orgCode: 'Organization Code',
        logout: 'Logout',
        newEmployee: 'New Employee',

        // Payslips
        loadingPayslips: 'Loading payslips...',
        noPayslips: 'No Payslips Available',
        noPayslipsDesc: 'Your payslips will appear here once payroll has been processed for your salary period.',
        netSalary: 'Net Salary',
        earnings: 'Earnings',
        basicSalary: 'Basic Salary',
        overtimePay: 'Overtime',
        allowances: 'Allowances',
        grossSalary: 'Gross Salary',
        deductions: 'Deductions',
        salaryAdvanceDeduction: 'Salary Advance',
        totalDeductions: 'Total Deductions',
        downloading: 'Downloading...',
        downloadPayslip: 'Download Payslip (PDF)',
        paid: 'Paid',
        confirmed: 'Confirmed',
        closed: 'Closed',
        prorated: 'Prorated',
        failedLoadPayslips: 'Failed to load payslips',

        // Settings
        settings: 'Settings',
        settingsDesc: 'Workplace configuration, shift schedule, and notification preferences',
        managedByOrg: 'Managed by Organization',
        managedByOrgDesc: 'Office locations and shift schedules are configured by your organization admin and cannot be modified here.',
        readOnly: 'Read-only',
        languageDesc: 'Choose your preferred display language',
        officeLocation: 'Office Location',
        officeLocationDesc: 'Office GPS coordinates and geofence boundary managed by your organization',
        loadingOfficeLocations: 'Loading office locations...',
        noOfficeLocations: 'No site assigned to you',
        noOfficeLocationsDesc: 'Contact your HR admin to assign office locations to your account.',
        gpsCoordinates: 'GPS Coordinates',
        geofenceRadius: 'Geofence Radius',
        meters: 'meters',
        mustBeWithin: (dist) => `You must be within ${dist} of the office to check in/out`,
        refreshLocations: 'Refresh locations',
        shiftSchedule: 'Shift Schedule',
        shiftScheduleDesc: 'Your work shift is assigned by your organization and cannot be modified',
        loadingShiftSchedule: 'Loading shift schedule...',
        notAssigned: 'Not Assigned',
        noShiftAssigned: 'No shift schedule assigned',
        noShiftAssignedDesc: 'Contact your organization admin to assign a shift.',
        activeShift: 'Active',
        upcomingShift: 'Upcoming',
        activeFrom: (date) => `Active from ${date}`,
        effectiveFrom: (date) => `Effective from ${date}`,
        refreshShiftSchedule: 'Refresh shift schedule',
        notificationsDesc: 'Check-in reminders & alerts',
        browserNotificationsUnsupported: 'Browser notifications are not supported.',
        dailyReminder: 'Daily Reminder',
        notifActive: 'Active',
        notifDisabled: 'Disabled',
        preShiftReminder: 'Pre-shift reminder',
        preShiftReminderDesc: '5 min before shift',
        lateCheckinAlert: 'Late check-in alert',
        lateCheckinAlertDesc: 'At shift start time',
        postCheckinConfirmation: 'Post check-in confirmation',
        postCheckinConfirmationDesc: 'When late is recorded',
        notifGranted: 'Granted',
        notifDenied: 'Denied',
        notifDefault: 'Not asked yet',
        notifUnsupported: 'Not supported',
        testNotif: 'Test',
        allowNotif: 'Allow',
        notifBlockedSettings: 'Notifications are blocked. Open your browser site settings and allow notifications, then reload.',
        appPermissions: 'App Permissions',
        appPermissionsDesc: 'Location and camera access required for check-in',
        locationDesc: 'Required for geofence check-in',
        cameraDesc: 'Required for selfie photo at check-in',
        toggleLocation: 'Toggle location permission',
        toggleCamera: 'Toggle camera permission',
        requesting: 'Requesting…',
        allowLocation: 'Allow Location Access',
        allowCamera: 'Allow Camera Access',
        locationGrantedInfo: 'To revoke: click the lock icon in your browser address bar → Site settings → set Location to Block.',
        locationDenied: 'Location is blocked. Click the lock icon in the address bar → Site settings → set Location to Allow, then reload.',
        cameraGrantedInfo: 'To revoke: click the lock icon in your browser address bar → Site settings → set Camera to Block.',
        cameraDenied: 'Camera is blocked. Click the lock icon in the address bar → Site settings → set Camera to Allow, then reload.',
        allPermissionsGranted: 'All permissions granted — check-in is fully functional.',
        refreshPermissions: 'Refresh permissions',
        permGranted: 'Granted',
        permDenied: 'Denied',
        permPrompt: 'Not asked yet',
        permUnsupported: 'Not supported',
        currentConfig: 'Current Configuration',
        // Camera Capture Modal
        cameraTitle: 'Take Check-In Photo',
        checkOutCameraTitle: 'Take Check-Out Photo',
        cameraModalDesc: 'Your photo will be recorded with your attendance entry.',
        cameraPermDenied: 'Camera permission denied. Please allow camera access and try again.',
        cameraNotFound: 'No camera found on this device.',
        cameraAccessFailed: 'Unable to access camera. Please check your settings.',
        openingCamera: 'Opening camera…',
        switchCamera: 'Switch camera',
        tryAgain: 'Try Again',
        openCamera: 'Open Camera',
        capturePhoto: 'Capture',
        retakePhoto: 'Retake',
        usePhoto: 'Use Photo',
        close: 'Close',
    },

    id: {
        // Common
        on: 'Aktif',
        off: 'Nonaktif',
        pending: 'menunggu',
        active: 'Aktif',
        start: 'Masuk',
        end: 'Keluar',
        total: 'total',
        hours: 'Jam',
        shift: 'Shift',
        office: 'Kantor',
        location: 'Lokasi',
        camera: 'Kamera',
        notifications: 'Notifikasi',
        coordinates: 'Koordinat',
        radius: 'Radius',
        loading: 'Memuat...',
        retry: 'Coba Lagi',
        refresh: 'Perbarui',
        cancel: 'Batal',
        submit: 'Kirim',
        onTime: 'Tepat Waktu',
        late: 'Terlambat',
        absent: 'Tidak Hadir',
        checkIn: 'Absen Masuk',
        checkOut: 'Absen Keluar',
        duration: 'Durasi',
        permission: 'Izin',
        language: 'Bahasa',
        english: 'English',
        indonesian: 'Bahasa Indonesia',
        error: 'Terjadi kesalahan',
        myAccount: 'Akun Saya',

        // Navigation
        navHome: 'Beranda',
        navAttendance: 'Kehadiran',
        navRequests: 'Pengajuan',
        navSettings: 'Pengaturan',
        navProfile: 'Profil',

        // Login
        loginTitle: 'Hadir',
        loginSubtitle: 'Sistem manajemen kehadiran pintar',
        loginWelcome: 'Selamat Datang',
        loginDesc: 'Masukkan kredensial Anda untuk mengakses akun',
        loginEmail: 'Email atau Username',
        loginEmailPlaceholder: 'john@perusahaan.com atau john123',
        loginPassword: 'Kata Sandi',
        loginPasswordPlaceholder: 'Masukkan kata sandi Anda',
        loginSignIn: 'Masuk',
        loginSigningIn: 'Sedang masuk...',
        loginNeedAccess: 'Butuh akses?',
        loginContactHR: 'Hubungi HR',
        developedBy: 'Dikembangkan oleh',
        loginFillFields: 'Harap isi semua kolom',
        loginSuccess: 'Selamat datang kembali!',
        loginSuccessDesc: 'Anda berhasil masuk.',
        loginFailed: 'Gagal Masuk',

        // Dashboard
        greetMorning: 'Selamat Pagi',
        greetAfternoon: 'Selamat Siang',
        greetEvening: 'Selamat Sore',
        greetNight: 'Selamat Malam',
        enableReminders: 'Aktifkan Pengingat Kehadiran',
        enableRemindersDesc: 'Dapatkan notifikasi 5 menit sebelum shift dimulai',
        enable: 'Aktifkan',
        remindersOn: 'Pengingat aktif',
        notifBlocked: 'Notifikasi diblokir',
        todaysAttendance: 'Kehadiran Hari Ini',
        notCheckedInYet: 'Belum Absen Masuk',
        notCheckedInDesc: 'Anda belum absen masuk hari ini',
        photoVerification: 'Foto akan diambil untuk verifikasi',
        checkInNow: 'Absen Masuk Sekarang',
        workComplete: 'Kerja Selesai',
        workCompleteDesc: 'Kerja bagus hari ini! Selamat beristirahat.',
        currentlyWorking: 'Sedang Bekerja',
        currentlyWorkingDesc: 'Anda sedang bekerja, pertahankan!',
        shiftProgress: 'Progres Shift',
        checkInTime: 'Jam Masuk',
        workingDuration: 'Durasi Kerja',
        checkOutBtn: 'Absen Keluar',
        liveLocation: 'Lokasi & Geofence',
        liveLocationDesc: 'Posisi Anda secara real-time relatif terhadap kantor. Anda harus berada dalam geofence untuk absen.',
        loadingMap: 'Memuat peta...',
        thisWeek: 'Minggu Ini',
        weeklyStreak: 'Rekapan kehadiran mingguan Anda',
        performance: 'Performa',
        thisMonthRate: 'Tingkat kehadiran bulan ini',
        onTimeDays: 'Hari Tepat Waktu',
        lateDays: 'Hari Terlambat',
        totalHours: 'Total Jam',
        todaysSchedule: 'Jadwal Hari Ini',
        regularShift: 'Shift Reguler',
        earlyCheckout: 'Konfirmasi Pulang Lebih Awal',
        earlyCheckoutDesc: (time, label) => `Waktu shift Anda hari ini berakhir pada pukul ${time}. Saat ini Anda bersiap untuk absen keluar ${label} lebih awal dari jadwal.`,
        earlyCheckoutWarning: 'Harap diperhatikan bahwa absen keluar pada jam ini akan tercatat sebagai kepulangan lebih awal pada riwayat kehadiran Anda.',
        earlyCheckoutConfirm: 'Apakah Anda yakin ingin melanjutkan absen keluar sekarang?',
        stayWorking: 'Batal & Lanjut Bekerja',
        checkOutEarly: 'Ya, Absen Keluar',
        checkInBlockedTitle: 'Tidak Dapat Absen Masuk',
        checkInBlockedSubtitle: 'Akun Anda belum sepenuhnya dikonfigurasi. Hubungi HR atau Admin Anda untuk menyelesaikan masalah berikut sebelum bisa absen masuk:',
        checkInBlockedNoSite: 'Lokasi Kantor Belum Ditetapkan',
        checkInBlockedNoSiteDesc: 'Anda belum ditetapkan ke lokasi kantor manapun. Sistem membutuhkan lokasi kantor yang valid untuk memverifikasi posisi geofence Anda saat absen masuk.',
        checkInBlockedNoShift: 'Jadwal Shift Belum Ditetapkan',
        checkInBlockedNoShiftDesc: 'Anda belum ditetapkan ke jadwal kerja (shift). Sistem membutuhkan jadwal shift untuk mencatat status tepat waktu atau terlambat pada kehadiran Anda.',
        checkInBlockedContact: 'Silakan hubungi HR atau Admin Anda untuk menetapkan konfigurasi yang diperlukan pada akun Anda.',
        checkInBlockedUnderstood: 'Mengerti',
        checkedInToast: 'Absen Masuk Berhasil!',
        checkedInDesc: 'Anda telah berhasil absen masuk hari ini.',
        checkInFailed: 'Absen Masuk Gagal',
        checkedOutToast: 'Absen Keluar Berhasil!',
        checkedOutDesc: 'Anda telah berhasil absen keluar. Selamat beristirahat!',
        checkOutFailed: 'Absen Keluar Gagal',
        refreshed: 'Diperbarui',
        refreshedDesc: 'Data telah diperbarui.',
        refreshFailed: 'Gagal Memperbarui',
        refreshFailedDesc: 'Tidak dapat memperbarui data.',
        notifEnabled: 'Notifikasi diaktifkan!',
        notifEnabledDesc: 'Anda akan diingatkan sebelum shift dimulai.',
        notifPermDenied: 'Izin ditolak',
        notifPermDeniedDesc: 'Aktifkan notifikasi di pengaturan browser Anda.',
        tooEarlyTitle: 'Belum Waktunya Absen',
        tooEarlyDesc: 'Anda baru bisa melakukan absen masuk paling cepat 1 jam sebelum jam shift dimulai.',
        tooEarlyGotIt: 'Mengerti',

        // Dashboard widgets
        dashPendingRequests: 'Pengajuan Menunggu',
        dashPendingRequestsDesc: 'Pengajuan yang menunggu persetujuan',
        dashNoPending: 'Semua Beres!',
        dashNoPendingDesc: 'Tidak ada pengajuan yang menunggu saat ini.',
        dashViewAll: 'Lihat Semua',
        dashLatestPayslip: 'Slip Gaji Terbaru',
        dashLatestPayslipDesc: 'Slip gaji terakhir Anda',
        dashNoPayslip: 'Belum Ada Slip Gaji',
        dashNoPayslipDesc: 'Slip gaji akan muncul di sini setelah diproses.',
        dashViewPayslips: 'Lihat Slip Gaji',
        dashOvertimeThisMonth: 'Lembur Bulan Ini',
        dashOvertimeThisMonthDesc: 'Ringkasan lembur yang disetujui',
        dashApprovedHours: 'Jam Disetujui',
        dashApprovedRequests: 'Pengajuan Disetujui',
        dashNoOvertime: 'Tidak Ada Lembur',
        dashNoOvertimeDesc: 'Belum ada lembur tercatat bulan ini.',
        dashPendingOt: 'Menunggu',

        // Attendance
        attendanceHistory: 'Riwayat Kehadiran',
        attendanceHistoryDesc: 'Lihat dan lacak catatan kehadiran Anda',
        thisMonth: 'Bulan Ini',
        totalDaysWorked: 'Total Hari Kerja',
        average: 'Rata-rata',
        avgPerDay: (h) => `Rata-rata ${h}j/hari`,
        monthlyOverview: 'Ringkasan Bulanan',
        monthlyOverviewDesc: 'setiap kotak mewakili satu hari',
        upcoming: 'Akan Datang',
        filterAndTrend: 'Filter & Tren',
        all: 'Semua',
        month: 'Bulan',
        week: 'Minggu',
        totalRecords: 'Total Catatan',
        noRecords: 'Tidak Ada Catatan',
        noRecordsDesc: 'Anda tidak memiliki catatan kehadiran untuk periode yang dipilih. Coba ubah filter atau absen masuk untuk membuat catatan pertama.',
        record: 'catatan',
        records: 'catatan',
        checkInLocation: 'Lokasi Absen Masuk',
        checkOutLocation: 'Lokasi Absen Keluar',
        fromOffice: 'dari kantor',
        lateBy: (min) => `Terlambat ${min} menit`,
        earlyBy: (min) => `Lebih awal ${min} menit`,
        payslips: 'Slip Gaji',

        // Requests
        myRequests: 'Pengajuan Saya',
        myRequestsDesc: 'Ajukan dan lacak cuti, lembur, dan kasbon Anda',
        totalRequests: 'Total Pengajuan',
        pendingRequests: 'Menunggu',
        approved: 'Disetujui',
        rejected: 'Ditolak',
        cancelled: 'Dibatalkan',
        loadingRequests: 'Memuat pengajuan...',
        statusDistribution: 'Distribusi Status',
        requestStatusBreakdown: 'Rincian status pengajuan',
        requestsByType: 'Pengajuan per Tipe',
        requestTypeBreakdown: 'Distribusi berdasarkan jenis',
        leave: 'Cuti',
        overtime: 'Lembur',
        salaryAdv: 'Kasbon',
        leaveRequests: 'Pengajuan Cuti',
        leaveRequestsDesc: 'Ajukan cuti dari pekerjaan',
        newLeave: 'Cuti Baru',
        newLeaveTitle: 'Pengajuan Cuti Baru',
        newLeaveDesc: 'Isi detail di bawah untuk mengajukan cuti.',
        leaveType: 'Jenis Cuti',
        selectLeaveType: 'Pilih jenis cuti',
        startDate: 'Tanggal Mulai',
        endDate: 'Tanggal Selesai',
        reason: 'Alasan',
        reasonPlaceholder: 'Jelaskan alasan cuti Anda...',
        submitRequest: 'Kirim Pengajuan',
        overtimeRequests: 'Pengajuan Lembur',
        overtimeRequestsDesc: 'Catat jam kerja tambahan',
        newOvertime: 'Lembur Baru',
        newOvertimeTitle: 'Pengajuan Lembur Baru',
        newOvertimeDesc: 'Tentukan tanggal dan jam lembur yang dikerjakan.',
        date: 'Tanggal',
        otTotalHours: 'Total Jam',
        otMaxHours: 'Maksimal 12 jam per pengajuan',
        salaryAdvance: 'Kasbon',
        salaryAdvanceDesc: 'Ajukan pencairan gaji lebih awal',
        newRequest: 'Pengajuan Baru',
        newSalaryTitle: 'Pengajuan Kasbon Baru',
        newSalaryDesc: 'Tentukan jumlah dan alasan pengajuan kasbon Anda.',
        amount: 'Jumlah (Rp)',
        salaryReasonPlaceholder: 'Jelaskan mengapa Anda memerlukan kasbon...',
        noRequests: (type) => `Belum ada pengajuan ${type}`,
        noRequestsDesc: 'Klik tombol di atas untuk mengajukan pengajuan pertama Anda',
        submitted: (rel) => `Diajukan ${rel}`,
        from: 'Dari',
        to: 'Sampai',
        cancelRequest: 'Batalkan Pengajuan',
        cancelRequestDesc: 'Apakah Anda yakin ingin membatalkan pengajuan ini? Tindakan ini tidak dapat dibatalkan.',
        keepRequest: 'Pertahankan',
        yesCancel: 'Ya, Batalkan',
        fillAllFields: 'Harap isi semua kolom',
        endDateAfterStart: 'Tanggal selesai harus setelah tanggal mulai',
        fillValidValues: 'Harap isi semua kolom dengan nilai yang valid',
        otMaxExceeded: 'Lembur tidak boleh melebihi 12 jam',
        leaveSubmitted: 'Pengajuan cuti berhasil dikirim',
        overtimeSubmitted: 'Pengajuan lembur berhasil dikirim',
        salarySubmitted: 'Pengajuan kasbon berhasil dikirim',
        shiftSwapSubmitted: 'Pengajuan tukar shift berhasil dikirim',

        // Shift Swap
        shiftSwap: 'Tukar Shift',
        newShiftSwap: 'Tukar Baru',
        newShiftSwapTitle: 'Ajukan Tukar Shift',
        newShiftSwapDesc: 'Pilih rekan dan tanggal untuk tukar shift',
        swapDate: 'Tanggal Tukar',
        targetEmployee: 'Tukar Dengan',
        selectColleague: 'Pilih rekan kerja...',
        swapReason: 'Alasan (Opsional)',
        swapReasonPlaceholder: 'Mengapa kamu perlu tukar shift?',
        shiftSwapRejected: 'Ditolak',
        noShiftSwapRequests: 'Belum ada permintaan tukar shift',
        requesterShift: 'Shift Kamu',
        targetShift: 'Shift Mereka',
        loadingColleagues: 'Memuat rekan kerja...',
        noColleaguesFound: 'Rekan kerja tidak ditemukan',
        selectDateFirst: 'Pilih tanggal tukar terlebih dahulu',

        // Profile
        punctuality: 'Ketepatan Waktu',
        daysAttended: 'Hari Hadir',
        leavesUsed: 'Cuti Dipakai',
        personalInfo: 'Informasi Pribadi',
        personalInfoDesc: 'Detail pribadi Anda',
        email: 'Email',
        phone: 'Telepon',
        birthDate: 'Tanggal Lahir',
        gender: 'Jenis Kelamin',
        address: 'Alamat',
        notProvided: 'Belum diisi',
        employmentDetails: 'Detail Kepegawaian',
        employmentDetailsDesc: 'Informasi pekerjaan dan identitas Anda',
        employeeCode: 'Kode Karyawan',
        department: 'Departemen',
        organization: 'Organisasi',
        joined: 'Bergabung',
        performanceTitle: 'Performa',
        days: 'hari',
        avgHoursDay: 'Rata-rata Jam/Hari',
        accountIds: 'ID Akun',
        userUuid: 'UUID Pengguna',
        orgCode: 'Kode Organisasi',
        logout: 'Keluar',
        newEmployee: 'Karyawan Baru',

        // Payslips
        loadingPayslips: 'Memuat slip gaji...',
        noPayslips: 'Belum Ada Slip Gaji',
        noPayslipsDesc: 'Slip gaji Anda akan muncul di sini setelah penggajian diproses.',
        netSalary: 'Gaji Bersih',
        earnings: 'Pendapatan',
        basicSalary: 'Gaji Pokok',
        overtimePay: 'Lembur',
        allowances: 'Tunjangan',
        grossSalary: 'Gaji Kotor',
        deductions: 'Potongan',
        salaryAdvanceDeduction: 'Kasbon',
        totalDeductions: 'Total Potongan',
        downloading: 'Mengunduh...',
        downloadPayslip: 'Unduh Slip Gaji (PDF)',
        paid: 'Dibayar',
        confirmed: 'Dikonfirmasi',
        closed: 'Ditutup',
        prorated: 'Prorata',
        failedLoadPayslips: 'Gagal memuat slip gaji',

        // Settings
        settings: 'Pengaturan',
        settingsDesc: 'Konfigurasi tempat kerja, jadwal shift, dan preferensi notifikasi',
        managedByOrg: 'Dikelola oleh Organisasi',
        managedByOrgDesc: 'Lokasi kantor dan jadwal shift dikonfigurasi oleh admin organisasi dan tidak dapat diubah di sini.',
        readOnly: 'Hanya Baca',
        languageDesc: 'Pilih bahasa tampilan yang Anda inginkan',
        officeLocation: 'Lokasi Kantor',
        officeLocationDesc: 'Koordinat GPS kantor dan batas geofence yang dikelola oleh organisasi Anda',
        loadingOfficeLocations: 'Memuat lokasi kantor...',
        noOfficeLocations: 'Belum ada site yang ditugaskan',
        noOfficeLocationsDesc: 'Hubungi admin HR untuk menugaskan lokasi kantor ke akun Anda.',
        gpsCoordinates: 'Koordinat GPS',
        geofenceRadius: 'Radius Geofence',
        meters: 'meter',
        mustBeWithin: (dist) => `Anda harus berada dalam radius ${dist} dari kantor untuk absen masuk/keluar`,
        refreshLocations: 'Perbarui lokasi',
        shiftSchedule: 'Jadwal Shift',
        shiftScheduleDesc: 'Jadwal shift Anda ditetapkan oleh organisasi dan tidak dapat diubah',
        loadingShiftSchedule: 'Memuat jadwal shift...',
        notAssigned: 'Belum Ditugaskan',
        noShiftAssigned: 'Belum ada jadwal shift',
        noShiftAssignedDesc: 'Hubungi admin organisasi untuk menetapkan jadwal shift.',
        activeShift: 'Aktif',
        upcomingShift: 'Mendatang',
        activeFrom: (date) => `Aktif sejak ${date}`,
        effectiveFrom: (date) => `Berlaku mulai ${date}`,
        refreshShiftSchedule: 'Perbarui jadwal shift',
        notificationsDesc: 'Pengingat absen & peringatan',
        browserNotificationsUnsupported: 'Browser tidak mendukung notifikasi.',
        dailyReminder: 'Pengingat Harian',
        notifActive: 'Aktif',
        notifDisabled: 'Nonaktif',
        preShiftReminder: 'Pengingat sebelum shift',
        preShiftReminderDesc: '5 menit sebelum shift',
        lateCheckinAlert: 'Peringatan absen terlambat',
        lateCheckinAlertDesc: 'Saat jam mulai shift',
        postCheckinConfirmation: 'Konfirmasi absen masuk',
        postCheckinConfirmationDesc: 'Ketika keterlambatan tercatat',
        notifGranted: 'Diberikan',
        notifDenied: 'Ditolak',
        notifDefault: 'Belum diminta',
        notifUnsupported: 'Tidak didukung',
        testNotif: 'Tes',
        allowNotif: 'Izinkan',
        notifBlockedSettings: 'Notifikasi diblokir. Buka pengaturan situs browser Anda dan izinkan notifikasi, lalu muat ulang.',
        appPermissions: 'Izin Aplikasi',
        appPermissionsDesc: 'Akses lokasi dan kamera diperlukan untuk absen',
        locationDesc: 'Diperlukan untuk absen berbasis geofence',
        cameraDesc: 'Diperlukan untuk foto selfie saat absen masuk',
        toggleLocation: 'Aktifkan/nonaktifkan izin lokasi',
        toggleCamera: 'Aktifkan/nonaktifkan izin kamera',
        requesting: 'Meminta...',
        allowLocation: 'Izinkan Akses Lokasi',
        allowCamera: 'Izinkan Akses Kamera',
        locationGrantedInfo: 'Untuk mencabut: klik ikon kunci 🔒 di bilah alamat browser → Pengaturan situs → atur Lokasi ke Blokir.',
        locationDenied: 'Lokasi diblokir. Klik ikon kunci di bilah alamat → Pengaturan situs → atur Lokasi ke Izinkan, lalu muat ulang.',
        cameraGrantedInfo: 'Untuk mencabut: klik ikon kunci 🔒 di bilah alamat browser → Pengaturan situs → atur Kamera ke Blokir.',
        cameraDenied: 'Kamera diblokir. Klik ikon kunci di bilah alamat → Pengaturan situs → atur Kamera ke Izinkan, lalu muat ulang.',
        allPermissionsGranted: 'Semua izin diberikan — absen berjalan penuh.',
        refreshPermissions: 'Perbarui izin',
        permGranted: 'Diberikan',
        permDenied: 'Ditolak',
        permPrompt: 'Belum diminta',
        permUnsupported: 'Tidak didukung',
        currentConfig: 'Konfigurasi Saat Ini',
        // Camera Capture Modal
        cameraTitle: 'Ambil Foto Check-In',
        checkOutCameraTitle: 'Ambil Foto Check-Out',
        cameraModalDesc: 'Foto Anda akan dicatat bersama data kehadiran.',
        cameraPermDenied: 'Izin kamera ditolak. Harap izinkan akses kamera dan coba lagi.',
        cameraNotFound: 'Tidak ada kamera yang ditemukan di perangkat ini.',
        cameraAccessFailed: 'Tidak dapat mengakses kamera. Periksa pengaturan Anda.',
        openingCamera: 'Membuka kamera…',
        switchCamera: 'Ganti kamera',
        tryAgain: 'Coba Lagi',
        openCamera: 'Buka Kamera',
        capturePhoto: 'Ambil Foto',
        retakePhoto: 'Foto Ulang',
        usePhoto: 'Gunakan Foto',
        close: 'Tutup',
    },
};

// ─── Context ──────────────────────────────────────────────────────────────────

interface LanguageContextValue {
    language: Language;
    setLanguage: (lang: Language) => void;
    t: TranslationKeys;
}

const LanguageContext = createContext<LanguageContextValue | null>(null);

export function LanguageProvider({ children }: { children: React.ReactNode }): React.JSX.Element {
    const [language, setLanguageState] = useState<Language>(() => {
        const stored = localStorage.getItem(STORAGE_KEY);
        return (stored === 'en' || stored === 'id') ? stored : 'id';
    });

    const setLanguage = useCallback((lang: Language) => {
        setLanguageState(lang);
        localStorage.setItem(STORAGE_KEY, lang);
    }, []);

    const t = translations[language];

    return (
        <LanguageContext.Provider value={{ language, setLanguage, t }}>
            {children}
        </LanguageContext.Provider>
    );
}

export function useLanguage(): LanguageContextValue {
    const ctx = useContext(LanguageContext);
    if (!ctx) throw new Error('useLanguage must be used inside LanguageProvider');
    return ctx;
}
